import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";

import { MENU, FINANCE_SETTINGS_BASE } from "./config";
import { useExpensesAccess } from "../expenses/hooks/useExpensesAccess";
import { useMenuEntitlement } from "../../hooks/useEntitlement";

// Shell de la app «Configuración» de Finanzas internas (#1255): categorías y
// tipo de cambio que usan las apps de finanzas. Espeja ExpensesLayout y usa su
// misma puerta: solo entra quien tiene acceso a Finanzas (en CC-CONSOLE, los
// dueños); al resto se le explica, sin llamar a la API.
export default function FinanceSettingsLayout() {
  // Ítems de menú por derecho (`menu:finance-settings.<id>`).
  const canSeeMenuItem = useMenuEntitlement("finance-settings");
  const { t } = useTranslation("common");
  const { themeMode, isDarkMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } =
    useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  // Sin empresa elegida (administrador de reseller) no se llama a la API: el 403
  // cerraría la sesión de toda la app.
  const hasCompany = Boolean(localStorage.getItem("companyId") || localStorage.getItem("userCompanyId"));

  const access = useExpensesAccess(true, { fresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({
        title: t("appsSidebar.comingSoon"),
        text: t("appsSidebar.comingSoonText", { name: app.name }),
        icon: "info",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.isLocked) {
      Swal.fire({
        title: t("appsSidebar.premiumFeature"),
        text: t("appsSidebar.premiumRequired", { name: app.name }),
        icon: "warning",
        confirmButtonText: t("appsSidebar.understood"),
        confirmButtonColor: brand.primary,
      });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const menuItems = MENU.filter((item) => canSeeMenuItem(item.id)).map((item) => ({
    id: item.id,
    label: t(item.labelKey),
    icon: item.icon,
    path: `${FINANCE_SETTINGS_BASE}/${item.id}`,
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="flex h-screen pt-safe-top flex-col bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header title={t("financeSettings.appName")} subtitle={t("financeSettings.appSubtitle")} onOpenAppsSidebar={openAppsSidebar} />

      <div className="flex min-h-0 flex-1">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => location.pathname === `${FINANCE_SETTINGS_BASE}/${itemId}`}
          footerStats={{
            label: t("financeSettings.appName"),
            items: [{ label: t("financeSettings.sections"), value: menuItems.length }],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          {access !== "allowed" ? (
            <div className="flex flex-col items-center justify-center text-center gap-2 p-10" data-testid="no-access">
              {access === "unknown" ? (
                <i className="ri-loader-4-line animate-spin text-3xl text-teal-600"></i>
              ) : (
                <>
                  <i className={`ri-lock-2-line text-3xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
                  <p className={`text-lg font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
                    {t(access === "error" ? "expenses.noAccess.errorTitle" : "financeSettings.noAccess.title")}
                  </p>
                  <p className={`text-sm max-w-md ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {t(access === "error" ? "expenses.noAccess.errorText" : "financeSettings.noAccess.text")}
                  </p>
                </>
              )}
            </div>
          ) : hasCompany ? (
            <Outlet />
          ) : (
            <div className="flex flex-col items-center justify-center text-center gap-2 p-10" data-testid="no-company">
              <i className={`ri-building-line text-3xl ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}></i>
              <p className={`text-lg font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>{t("expenses.noCompany.title")}</p>
              <p className={`text-sm max-w-md ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{t("expenses.noCompany.text")}</p>
            </div>
          )}
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId="finance-settings"
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={
          user
            ? {
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                username: user.username,
              }
            : undefined
        }
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
